import AcademicYearLockNotice from '../components/common/AcademicYearLockNotice';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import AutoStatusToast from '../components/common/AutoStatusToast';
import DeleteHoldButton from '../components/common/DeleteHoldButton';
import DailyNoteCategoryDropdown from '../components/dailyNote/DailyNoteCategoryDropdown';
import DailyNoteLockModal, { type DailyNoteLockModalMode } from '../components/dailyNote/DailyNoteLockModal';
import DailyNoteLockedPreview from '../components/dailyNote/DailyNoteLockedPreview';
import PointItemDateCalendarModal from '../components/pointItem/PointItemDateCalendarModal';
import TodoCategoryCreateModal from '../components/todoNote/TodoCategoryCreateModal';
import { SortableTodoCategoryChip } from '../components/todoNote/SortableTodoCategoryChip';
import { rowBackgroundStyle } from '../components/todoNote/todoNoteRowShared';
import { useAutoStatusToast } from '../hooks/useAutoStatusToast';
import {
  deleteDailyRecordNote,
  fetchDailyRecordNotesPage,
  peekDailyRecordNotesPageCache,
  upsertDailyRecordNote,
  dailyNotesTotalPages,
} from '../services/dailyRecordNotesService';
import {
  deleteDailyNoteCategory,
  fetchDailyNoteCategories,
  peekDailyNoteCategoriesCache,
  saveDailyNoteCategory,
  saveDailyNoteCategoryOrder,
} from '../services/dailyNoteCategoriesService';
import { DAILY_NOTES_PAGE1_SIZE } from '../utils/dailyRecordNotesCache';
import { supabase } from '../supabaseClient';
import type { DailyNoteCategoryRow, DailyRecordNoteRow } from '../types/dailyRecordNote';
import { DEFAULT_DAILY_CATEGORY_NAME } from '../types/dailyRecordNote';
import { applyDailyCategoryModalConfirm, canEditDailyCategoryColor, isBuiltInDailyCategory, isDefaultDailyCategoryName, isGeneralDailyCategory, normalizeDailyCategoryName } from '../utils/dailyNoteCategoryEdit';
import { toISODate } from '../utils/date';
import { persistDailyNoteViewLockRemote, syncDailyNoteViewLockFromRemote } from '../services/dailyNoteViewLockRemote';
import { useDailyNoteViewLockEnabled } from '../hooks/useDailyNoteViewLockEnabled';
import {
  dailyNoteLockPasswordMatches,
  hashDailyNoteLockPassword,
  loadDailyNoteViewLock,
  saveDailyNoteViewLock,
  setDailyNoteViewSessionUnlocked,
} from '../utils/dailyNoteViewLockStorage';
import { loginEmailsMatch, maskLoginEmailHint } from '../utils/loginEmailMask';

const WEEKDAY_EN = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'] as const;
const DELETE_HOLD_MS = 1000;

function formatNoteDateLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const dt = new Date(y, m - 1, d);
  const en = WEEKDAY_EN[dt.getDay()] ?? '';
  return `${y}.${String(m).padStart(2, '0')}.${String(d).padStart(2, '0')} (${en})`;
}

function previewBody(body: string): string {
  const t = body.trim();
  if (!t) return '(내용 없음)';
  return t.length > 120 ? `${t.slice(0, 120)}…` : t;
}

type EditorState = {
  noteDate: string;
  body: string;
  categoryId: string | null;
  /** DB에 이미 저장된 행이 있으면 true (휴지통 활성) */
  persisted: boolean;
  persistedId: string | null;
  baselineDate: string;
  baselineBody: string;
  baselineCategoryId: string | null;
};

const DailyNotePage: React.FC = () => {
  const [userId, setUserId] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [notes, setNotes] = useState<DailyRecordNoteRow[]>([]);
  const [categories, setCategories] = useState<DailyNoteCategoryRow[]>([]);
  const [filterCategoryId, setFilterCategoryId] = useState<string | null>(null);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [categoryDeleteModal, setCategoryDeleteModal] = useState<{ id: string; name: string } | null>(
    null
  );
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openingNew, setOpeningNew] = useState(false);
  const [viewLockHash, setViewLockHash] = useState('');
  const [lockModal, setLockModal] = useState<DailyNoteLockModalMode | null>(null);
  const [lockModalBusy, setLockModalBusy] = useState(false);
  const [lockModalError, setLockModalError] = useState('');
  const { toastState, showSaving, showSuccess, showError } = useAutoStatusToast(2800);
  const { enabled: viewLockEnabled, contentsHidden } = useDailyNoteViewLockEnabled(userId);

  const isDirty = Boolean(
    editor &&
      (editor.noteDate !== editor.baselineDate ||
        editor.body !== editor.baselineBody ||
        (editor.categoryId ?? '') !== (editor.baselineCategoryId ?? ''))
  );

  const totalPages = dailyNotesTotalPages(totalCount);
  const safePage = Math.min(Math.max(1, page), totalPages);
  const showAddCard = safePage === 1;

  const loadPage = useCallback(
    async (
      uid: string,
      pageNum: number,
      options?: { forceRefresh?: boolean; cacheOnly?: boolean; categoryId?: string | null }
    ) => {
      const categoryId = options?.categoryId !== undefined ? options.categoryId : filterCategoryId;
      const peeked =
        !options?.forceRefresh ? peekDailyRecordNotesPageCache(uid, pageNum, categoryId) : null;
      if (peeked) {
        setNotes(peeked.notes);
        setTotalCount(peeked.totalCount);
        setPage(pageNum);
        if (options?.cacheOnly) {
          return peeked;
        }
      }

      const result = await fetchDailyRecordNotesPage(uid, pageNum, { ...options, categoryId });
      setNotes(result.notes);
      setTotalCount(result.totalCount);
      setPage(result.page);
      return result;
    },
    [filterCategoryId]
  );

  useEffect(() => {
    let cancelled = false;
    let pageStarted = false;

    const applyUser = (
      uid: string,
      email: string,
      options: { startLoad: boolean; syncRemote: boolean }
    ) => {
      if (cancelled || !uid) return;
      setUserId(uid);
      setUserEmail(email.trim());
      const lock = loadDailyNoteViewLock(uid);
      setViewLockHash(lock.passwordHash);
      if (options.syncRemote) {
        void syncDailyNoteViewLockFromRemote(uid).then((merged) => {
          if (cancelled) return;
          setViewLockHash(merged.passwordHash);
        });
      }
      if (!options.startLoad || pageStarted) return;
      pageStarted = true;
      const peekedCats = peekDailyNoteCategoriesCache(uid);
      if (peekedCats) setCategories(peekedCats);
      void (async () => {
        try {
          const cats = await fetchDailyNoteCategories(uid);
          if (cancelled) return;
          setCategories(cats);
          await loadPage(uid, 1);
        } catch (e) {
          if (cancelled) return;
          showError(e instanceof Error ? e.message : '불러오기에 실패했습니다.');
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    };

    void supabase.auth.getSession().then(({ data }) => {
      const user = data.session?.user;
      if (!user || cancelled) return;
      applyUser(user.id, user.email ?? '', {
        startLoad: true,
        syncRemote: false,
      });
    });

    void supabase.auth.getUser().then(({ data }) => {
      if (cancelled) return;
      const user = data.user;
      if (!user) {
        if (!pageStarted) setLoading(false);
        return;
      }
      applyUser(user.id, user.email ?? '', {
        startLoad: !pageStarted,
        syncRemote: true,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [loadPage, showError]);

  const sortedCategories = useMemo(
    () =>
      [...categories].sort(
        (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name) || a.id.localeCompare(b.id)
      ),
    [categories]
  );
  const defaultCategory = useMemo(
    () => sortedCategories.find((c) => isGeneralDailyCategory(c)) ?? sortedCategories[0] ?? null,
    [sortedCategories]
  );
  const categoryById = useMemo(() => {
    const map = new Map<string, DailyNoteCategoryRow>();
    for (const c of categories) map.set(c.id, c);
    return map;
  }, [categories]);
  const categoryDndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const goToPage = async (nextPage: number) => {
    if (!userId || pageLoading) return;
    const target = Math.min(Math.max(1, nextPage), totalPages);
    if (target === safePage && notes.length > 0) return;
    setPageLoading(true);
    try {
      await loadPage(userId, target, { cacheOnly: true });
    } catch (e) {
      showError(e instanceof Error ? e.message : '불러오기에 실패했습니다.');
    } finally {
      setPageLoading(false);
    }
  };

  const refreshAfterMutation = async () => {
    if (!userId) return;
    const result = await loadPage(userId, 1, { forceRefresh: true });
    return result;
  };

  const closeLockModal = () => {
    setLockModal(null);
    setLockModalBusy(false);
    setLockModalError('');
  };

  const requestViewUnlock = (_note?: DailyRecordNoteRow) => {
    setLockModalError('');
    setLockModal('note');
  };

  const handleHeaderLockClick = () => {
    if (!userId) return;
    setLockModalError('');
    setLockModal(viewLockEnabled ? 'email' : 'set');
  };

  const handleLockModalSubmit = async (value: string, confirmPassword?: string) => {
    if (!userId || lockModalBusy || !lockModal) return;
    const trimmed = value.trim();
    setLockModalBusy(true);
    setLockModalError('');
    try {
      if (lockModal === 'set') {
        if (trimmed.length < 4) {
          setLockModalError('잠금 비밀번호는 4자 이상이어야 합니다.');
          return;
        }
        if (trimmed !== (confirmPassword ?? '').trim()) {
          setLockModalError('비밀번호가 일치하지 않습니다.');
          return;
        }
        const passwordHash = await hashDailyNoteLockPassword(userId, trimmed);
        const next = {
          enabled: true,
          passwordHash,
          updatedAt: Date.now(),
        };
        try {
          await persistDailyNoteViewLockRemote(userId, next);
        } catch (e) {
          setLockModalError(
            e instanceof Error ? e.message : '잠금 설정을 서버에 저장하지 못했습니다.'
          );
          return;
        }
        saveDailyNoteViewLock(userId, next);
        setViewLockHash(passwordHash);
        setDailyNoteViewSessionUnlocked(userId, false);
        closeLockModal();
        showSuccess('데일리 노트 보기 잠금이 설정되었습니다.');
        return;
      }
      if (lockModal === 'email') {
        if (!userEmail) {
          setLockModalError('이 계정은 로그인 이메일이 없어 잠금을 해제할 수 없습니다.');
          return;
        }
        if (!loginEmailsMatch(trimmed, userEmail)) {
          setLockModalError('로그인 이메일이 올바르지 않습니다.');
          return;
        }
        const next = {
          enabled: false,
          passwordHash: viewLockHash,
          updatedAt: Date.now(),
        };
        try {
          await persistDailyNoteViewLockRemote(userId, next);
        } catch (e) {
          setLockModalError(
            e instanceof Error ? e.message : '잠금 해제를 서버에 저장하지 못했습니다.'
          );
          return;
        }
        saveDailyNoteViewLock(userId, next);
        setDailyNoteViewSessionUnlocked(userId, false);
        closeLockModal();
        showSuccess('데일리 노트 보기 잠금이 해제되었습니다.');
        return;
      }
      const ok = await dailyNoteLockPasswordMatches(userId, trimmed, viewLockHash);
      if (!ok) {
        setLockModalError('잠금 비밀번호가 올바르지 않습니다.');
        return;
      }
      setDailyNoteViewSessionUnlocked(userId, true);
      closeLockModal();
    } catch (e) {
      setLockModalError(e instanceof Error ? e.message : '확인에 실패했습니다.');
    } finally {
      setLockModalBusy(false);
    }
  };

  const openExisting = (note: DailyRecordNoteRow) => {
    if (contentsHidden && note.body.trim()) {
      requestViewUnlock();
      return;
    }
    setConfirmDiscard(false);
    setEditor({
      noteDate: note.note_date,
      body: note.body,
      categoryId: note.category_id,
      persisted: true,
      persistedId: note.id,
      baselineDate: note.note_date,
      baselineBody: note.body,
      baselineCategoryId: note.category_id,
    });
  };

  const openNew = async () => {
    if (!userId || openingNew) return;
    const today = toISODate(new Date());
    setConfirmDiscard(false);
    const categoryId = filterCategoryId || defaultCategory?.id || null;
    setEditor({
      noteDate: today,
      body: '',
      categoryId,
      persisted: false,
      persistedId: null,
      baselineDate: today,
      baselineBody: '',
      baselineCategoryId: categoryId,
    });
  };

  const closeEditor = () => {
    setCalendarOpen(false);
    setConfirmDiscard(false);
    setEditor(null);
  };

  const requestCloseEditor = () => {
    if (saving) return;
    if (isDirty) {
      setConfirmDiscard(true);
      return;
    }
    closeEditor();
  };

  const applyDate = async (iso: string) => {
    if (!editor) return;
    setEditor((prev) => (prev ? { ...prev, noteDate: iso } : prev));
  };

  const handleSave = async (options?: { closeAfter?: boolean }) => {
    if (!userId || !editor || saving) return;
    if (!editor.noteDate) {
      showError('날짜를 선택해 주세요.');
      return;
    }
    if (!editor.body.trim()) {
      showError('내용을 입력해 주세요.');
      return;
    }
    setSaving(true);
    showSaving();
    try {
      const saved = await upsertDailyRecordNote(userId, editor.noteDate, editor.body, {
        id: editor.persistedId,
        categoryId: editor.categoryId || defaultCategory?.id || null,
      });
      await refreshAfterMutation();
      if (options?.closeAfter) {
        closeEditor();
      } else {
        setEditor({
          noteDate: saved.note_date,
          body: saved.body,
          categoryId: saved.category_id,
          persisted: true,
          persistedId: saved.id,
          baselineDate: saved.note_date,
          baselineBody: saved.body,
          baselineCategoryId: saved.category_id,
        });
        setConfirmDiscard(false);
      }
      showSuccess('저장되었습니다.');
    } catch (e) {
      showError(e instanceof Error ? e.message : '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePersistedNote = useCallback(() => {
    void (async () => {
      if (!userId || !editor?.persisted || !editor.persistedId) return;
      try {
        await deleteDailyRecordNote(userId, editor.persistedId);
        await loadPage(userId, 1, { forceRefresh: true });
        showSuccess('삭제되었습니다.');
        setCalendarOpen(false);
        setConfirmDiscard(false);
        setEditor(null);
      } catch (e) {
        showError(e instanceof Error ? e.message : '삭제에 실패했습니다.');
      }
    })();
  }, [userId, editor, loadPage, showSuccess, showError]);

  const dateLabel = useMemo(
    () => (editor ? formatNoteDateLabel(editor.noteDate) : ''),
    [editor]
  );

  const handleCategoryDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id || !userId) return;
    const sorted = [...sortedCategories];
    const oldIndex = sorted.findIndex((c) => c.id === String(active.id));
    const newIndex = sorted.findIndex((c) => c.id === String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(sorted, oldIndex, newIndex).map((c, i) => ({ ...c, sort_order: i }));
    setCategories(next);
    void saveDailyNoteCategoryOrder(
      userId,
      next.map((c) => c.id)
    ).catch((err) => showError(err instanceof Error ? err.message : '카테고리 순서를 저장하지 못했습니다.'));
  };

  const confirmCategoryModal = (payload: { name: string; color: string; categoryId?: string | null }) => {
    if (!userId) return;
    const next = applyDailyCategoryModalConfirm({
      categories,
      payload,
      userId,
      generateId: () => crypto.randomUUID(),
      nowIso: new Date().toISOString(),
    });
    if (!next) {
      showError('카테고리 이름을 확인해 주세요.');
      return;
    }
    const named = normalizeDailyCategoryName(payload.name);
    const changed =
      (payload.categoryId ? next.find((c) => c.id === payload.categoryId) : undefined) ??
      next.find((c) => normalizeDailyCategoryName(c.name) === named);
    setCategories(next);
    if (changed) {
      void saveDailyNoteCategory(userId, changed).catch((err) =>
        showError(err instanceof Error ? err.message : '카테고리를 저장하지 못했습니다.')
      );
    }
  };

  const removeCategory = (id: string) => {
    if (!userId) return;
    void deleteDailyNoteCategory(userId, id)
      .then((next) => {
        setCategories(next);
        if (filterCategoryId === id) setFilterCategoryId(null);
        setEditor((prev) =>
          prev && prev.categoryId === id
            ? { ...prev, categoryId: defaultCategory?.id ?? null }
            : prev
        );
        return loadPage(userId, 1, { forceRefresh: true, categoryId: filterCategoryId === id ? null : filterCategoryId });
      })
      .catch((err) => showError(err instanceof Error ? err.message : '카테고리를 삭제하지 못했습니다.'));
    setCategoryDeleteModal(null);
  };

  const selectFilter = (id: string | null) => {
    setFilterCategoryId(id);
    if (!userId) return;
    void loadPage(userId, 1, { categoryId: id }).catch((err) =>
      showError(err instanceof Error ? err.message : '불러오기에 실패했습니다.')
    );
  };

  const editingCategory = editingCategoryId
    ? categories.find((c) => c.id === editingCategoryId)
    : null;

  return (
    <div className="cp-daily-note-page">
      <AcademicYearLockNotice label="일일기록" />
      <header className="cp-daily-note-header">
        <div className="cp-daily-note-header-text">
          <h1 className="cp-daily-note-title">데일리 노트</h1>
          <p className="cp-daily-note-desc">날짜·카테고리별로 여러 건의 메모·기록을 남길 수 있습니다.</p>
        </div>
        {userId ? (
          <button
            type="button"
            className={'cp-daily-note-lock-btn' + (viewLockEnabled ? ' is-locked' : '')}
            onClick={handleHeaderLockClick}
            aria-pressed={viewLockEnabled}
            aria-label={viewLockEnabled ? '데일리 노트 보기 잠금 해제' : '데일리 노트 보기 잠금 설정'}
            title={viewLockEnabled ? '보기 잠금 해제' : '보기 잠금 설정'}
          >
            <img
              src={`${process.env.PUBLIC_URL || ''}/${viewLockEnabled ? 'lock.svg' : 'unlock.svg'}`}
              alt=""
              width={22}
              height={22}
            />
          </button>
        ) : null}
      </header>

      {userId && !loading ? (
        <div className="cp-daily-note-chips-wrap">
          <div className="cp-todo-note-chips" role="tablist" aria-label="카테고리 필터">
            <div className={`cp-todo-note-chip-row${filterCategoryId === null ? ' is-active' : ''}`}>
              <button
                type="button"
                role="tab"
                aria-selected={filterCategoryId === null}
                className="cp-todo-note-chip-main"
                onClick={() => selectFilter(null)}
              >
                전체
              </button>
            </div>
            <DndContext
              sensors={categoryDndSensors}
              collisionDetection={closestCenter}
              onDragEnd={handleCategoryDragEnd}
            >
              <SortableContext
                items={sortedCategories.map((c) => c.id)}
                strategy={horizontalListSortingStrategy}
              >
                {sortedCategories.map((c) => (
                  <SortableTodoCategoryChip
                    key={c.id}
                    id={c.id}
                    name={c.name}
                    color={c.color}
                    active={filterCategoryId === c.id}
                    showActions
                    deletable={!isBuiltInDailyCategory(c)}
                    onSelect={() => selectFilter(c.id)}
                    onEdit={() => {
                      setEditingCategoryId(c.id);
                      setCategoryModalOpen(true);
                    }}
                    onDelete={() => setCategoryDeleteModal({ id: c.id, name: c.name })}
                  />
                ))}
              </SortableContext>
            </DndContext>
            <button
              type="button"
              className="cp-todo-note-chip cp-todo-note-chip-add"
              aria-label="카테고리 추가"
              onClick={() => {
                setEditingCategoryId(null);
                setCategoryModalOpen(true);
              }}
            >
              +
            </button>
          </div>
        </div>
      ) : null}

      {loading ? (
        <p className="cp-daily-note-status" role="status">
          불러오는 중…
        </p>
      ) : !userId ? (
        <p className="cp-daily-note-status" role="alert">
          로그인이 필요합니다.
        </p>
      ) : (
        <>
          <div className="cp-daily-note-scroll">
            <div
              className={'cp-daily-note-grid' + (pageLoading ? ' is-page-loading' : '')}
              aria-busy={pageLoading || undefined}
            >
              {showAddCard ? (
                <button
                  type="button"
                  className="cp-daily-note-add-card"
                  onClick={() => void openNew()}
                  disabled={openingNew}
                  aria-label="데일리 노트 추가"
                >
                  <span className="cp-daily-note-add-circle" aria-hidden>
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                      <circle
                        cx="24"
                        cy="24"
                        r="23"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeDasharray="4 4"
                        fill="none"
                      />
                      <path
                        d="M24 14V34M14 24H34"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                </button>
              ) : null}
              {notes.map((note) => {
                const cat = note.category_id ? categoryById.get(note.category_id) : undefined;
                return (
                <button
                  key={note.id || note.note_date}
                  type="button"
                  className="cp-daily-note-card"
                  style={rowBackgroundStyle(cat?.color)}
                  onClick={() => openExisting(note)}
                  aria-label={
                    contentsHidden && note.body.trim()
                      ? `잠긴 데일리 노트 ${formatNoteDateLabel(note.note_date)}`
                      : undefined
                  }
                >
                  <div className="cp-daily-note-card-date">{formatNoteDateLabel(note.note_date)}</div>
                  {cat ? (
                    <div className="cp-daily-note-card-cat">
                      <span
                        className="cp-todo-note-chip-dot"
                        style={{
                          background: cat.color,
                          border:
                            isDefaultDailyCategoryName(cat.name) || cat.color.toLowerCase() === '#ffffff'
                              ? '1px solid color-mix(in srgb, var(--color-border) 78%, transparent)'
                              : 'none',
                        }}
                        aria-hidden
                      />
                      {cat.name}
                    </div>
                  ) : null}
                  {contentsHidden && note.body.trim() ? (
                    <DailyNoteLockedPreview />
                  ) : (
                    <div className="cp-daily-note-card-body">{previewBody(note.body)}</div>
                  )}
                </button>
                );
              })}
            </div>
          </div>

          {totalCount > DAILY_NOTES_PAGE1_SIZE ? (
            <div
              className="cp-daily-note-pager"
              role="navigation"
              aria-label="데일리 노트 페이지"
            >
              <button
                type="button"
                className="cp-daily-note-pager-btn"
                disabled={safePage <= 1 || pageLoading}
                aria-label="이전 페이지"
                onClick={() => void goToPage(safePage - 1)}
              >
                &lt;
              </button>
              <span className="cp-daily-note-pager-status">
                {safePage} / {totalPages}
                <span className="cp-daily-note-pager-count">({totalCount}건)</span>
              </span>
              <button
                type="button"
                className="cp-daily-note-pager-btn"
                disabled={safePage >= totalPages || pageLoading}
                aria-label="다음 페이지"
                onClick={() => void goToPage(safePage + 1)}
              >
                &gt;
              </button>
            </div>
          ) : null}

          <button
            type="button"
            className="cp-fab-save cp-daily-note-fab"
            onClick={() => void openNew()}
            disabled={openingNew}
            aria-label="데일리 노트 추가"
            title="새 데일리 노트"
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden
            >
              <path
                d="M12 5V19M5 12H19"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </>
      )}

      {editor
        ? createPortal(
            <div className="cp-modal-backdrop" role="presentation">
              <div
                className="cp-daily-note-editor"
                role="dialog"
                aria-modal="true"
                aria-labelledby="cp-daily-note-editor-title"
              >
                <div className="cp-daily-note-editor-top">
                  <div className="cp-daily-note-editor-meta">
                  <button
                    type="button"
                    id="cp-daily-note-editor-title"
                    className="cp-daily-note-editor-date"
                    onClick={() => setCalendarOpen(true)}
                    title="날짜 선택"
                  >
                    {dateLabel}
                  </button>
                  <DailyNoteCategoryDropdown
                    categories={sortedCategories}
                    value={editor.categoryId}
                    onChange={(nextId: string) => {
                      setEditor((prev) => (prev ? { ...prev, categoryId: nextId } : prev));
                    }}
                    ariaLabel="카테고리"
                    isDefaultName={isDefaultDailyCategoryName}
                  />
                  </div>
                  <div className="cp-daily-note-editor-actions">
                    <DeleteHoldButton
                      className="cp-daily-note-editor-trash"
                      holdBarClassName="cp-daily-note-editor-trash-bar"
                      holdMs={DELETE_HOLD_MS}
                      disabled={!editor.persisted || saving}
                      disabledTitle="저장된 기록만 삭제할 수 있습니다"
                      disabledAriaLabel="저장된 기록만 삭제할 수 있습니다"
                      title="짧게 누르면 확인 후 삭제 · 길게 누르면 바로 삭제"
                      ariaLabel="데일리 노트 삭제 (짧게: 확인, 길게: 바로 삭제)"
                      confirmTitle="이 데일리 노트를 삭제할까요?"
                      confirmDescription="삭제하면 되돌릴 수 없습니다."
                      onDelete={handleDeletePersistedNote}
                    >
                      <img
                        src={`${process.env.PUBLIC_URL || ''}/Trash.svg`}
                        alt=""
                        width={20}
                        height={20}
                      />
                    </DeleteHoldButton>
                    <button
                      type="button"
                      className="cp-daily-note-editor-close"
                      onClick={requestCloseEditor}
                      aria-label="닫기"
                    >
                      ×
                    </button>
                  </div>
                </div>
                <textarea
                  className="cp-daily-note-editor-textarea"
                  value={editor.body}
                  placeholder="메모·기록을 입력하세요"
                  onChange={(e) =>
                    setEditor((prev) => (prev ? { ...prev, body: e.target.value } : prev))
                  }
                />
                <div className="cp-daily-note-editor-footer">
                  <button
                    type="button"
                    className="cp-pin-modal-btn cp-pin-modal-btn--secondary"
                    onClick={requestCloseEditor}
                    disabled={saving}
                  >
                    닫기
                  </button>
                  <button
                    type="button"
                    className="cp-pin-modal-btn cp-pin-modal-btn--primary"
                    onClick={() => void handleSave()}
                    disabled={saving || !isDirty}
                    title={isDirty ? '저장하기' : '저장할 변경사항 없음'}
                  >
                    {saving ? '저장 중…' : isDirty ? '저장' : '변경 사항 없음'}
                  </button>
                </div>
              </div>

              {confirmDiscard ? (
                <div
                  className="cp-modal-backdrop"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="cp-daily-note-leave-title"
                  onClick={() => setConfirmDiscard(false)}
                >
                  <div className="cp-modal cp-leave-modal-box" onClick={(e) => e.stopPropagation()}>
                    <h3 id="cp-daily-note-leave-title" className="cp-leave-modal-title">
                      변경사항이 저장되지 않았습니다
                    </h3>
                    <p className="cp-leave-modal-desc">저장하지 않고 나가시겠습니까?</p>
                    <div className="cp-modal-footer cp-leave-modal-actions">
                      <button
                        type="button"
                        className="cp-btn-secondary"
                        onClick={closeEditor}
                        disabled={saving}
                      >
                        저장하지 않고 나가기
                      </button>
                      <button
                        type="button"
                        className="cp-btn-primary"
                        onClick={() => {
                          setConfirmDiscard(false);
                          void handleSave({ closeAfter: true });
                        }}
                        disabled={saving}
                      >
                        {saving ? '저장 중…' : '저장 하기'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>,
            document.body
          )
        : null}

      <PointItemDateCalendarModal
        open={calendarOpen}
        selectedDateIso={editor?.noteDate ?? toISODate(new Date())}
        onClose={() => setCalendarOpen(false)}
        onSelect={(iso) => {
          void applyDate(iso);
        }}
      />
      <AutoStatusToast state={toastState} />
      <DailyNoteLockModal
        open={lockModal != null}
        mode={lockModal ?? 'note'}
        busy={lockModalBusy}
        error={lockModalError}
        emailHint={lockModal === 'email' ? maskLoginEmailHint(userEmail) : ''}
        onClose={closeLockModal}
        onSubmit={(value, confirmPassword) => {
          void handleLockModalSubmit(value, confirmPassword);
        }}
      />
      <TodoCategoryCreateModal
        isOpen={categoryModalOpen}
        onClose={() => {
          setCategoryModalOpen(false);
          setEditingCategoryId(null);
        }}
        onConfirm={confirmCategoryModal}
        initialName={editingCategory?.name ?? ''}
        initialColor={editingCategory?.color ?? '#2e7d32'}
        categoryId={editingCategoryId}
        nameEditable
        colorEditable={editingCategory ? canEditDailyCategoryColor(editingCategory) : true}
        title={editingCategory ? '카테고리 수정' : '카테고리 만들기'}
        confirmLabel={editingCategoryId ? '수정' : '추가'}
      />
      {categoryDeleteModal ? (
        <div
          className="cp-modal-backdrop"
          role="presentation"
          onMouseDown={(e) => e.target === e.currentTarget && setCategoryDeleteModal(null)}
        >
          <div
            className="cp-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-daily-note-category-delete-title"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h3 id="cp-daily-note-category-delete-title">카테고리 삭제</h3>
            <div className="cp-modal-body">
              <p>
                `{categoryDeleteModal.name}` 카테고리를 삭제하면 해당 카테고리의 기록은 기본 카테고리(
                {DEFAULT_DAILY_CATEGORY_NAME})로 옮겨집니다.
                <br />
                계속하시겠습니까?
              </p>
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setCategoryDeleteModal(null)}>
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => removeCategory(categoryDeleteModal.id)}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DailyNotePage;
